// Externals
import type { BaseDataLayerV3, EventMapV3 } from '@makemydeal/dr-common-data-layer-utils';
import { buildCDLEventMiddlewareV3, buildReadyEventV3, buildUserEventV3 } from '@makemydeal/dr-common-data-layer-utils';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { MiddlewareAPI } from 'redux';

// Libraries
import { CHANGE_VEHICLE_TAB_CHANGED } from '@makemydeal/dr-activities-change-vehicle';
import { NavigationActions } from '@makemydeal/dr-activities-common';
import { tradeInCdlEvents } from './tradeInCdlEvents';

// Types

// Actions
import { Actions } from '../actionTypes/actionTypes';
import { FinancingActions } from '../financing/financingActionTypes';

// Consts
import type { CdlEventInfo } from '@makemydeal/dr-sales-checkin-types';
import { CustomerCardState, SCI_CDL_TAGS, routingConfig } from '@makemydeal/dr-sales-checkin-types';
import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';

// Utils
import { buildCdlV3ObjectFromState } from '@makemydeal/dr-sales-checkin-ui-utils';

let isCdlDataReady: boolean = false;
const eventsBeforeDataReady: Array<AnyFSA> = [];

const extractVehicleInfo = (source: any) => ({
    make: source.make ?? '',
    model: source.model ?? '',
    year: source.year ?? '',
    trim: source.trim ?? ''
});

export const buildEventWrapper = (eventData: CdlEventInfo) => (action: AnyFSA) => {
    if (action.type === Actions.BUY_THIS_VEHICLE) {
        const vehicle = action.payload.vehicle || {};
        return {
            ...buildUserEventV3(eventData),
            search: extractVehicleInfo(vehicle),
            vehicle
        };
    }
    return buildUserEventV3(eventData);
};

// dc-sci-058 fires from four possible destination routes depending on workflow status.
const dealSummaryTileTag = {
    element: 'Deal Summary Tile',
    location: ['Left Rail Navigation', 'Build Your Offer'],
    intention: 'Review and submit deal information',
    tagIndex: 'dc-sci-058'
};

// Showroom nav tiles/links — shared by every NAVIGATE_TO dispatch, keyed on destination route
const navigateToRouteTagMap: Record<string, { element: string; location: string[]; intention: string; tagIndex: string }> = {
    [routingConfig.VEHICLE_DETAILS]: {
        element: 'Vehicle Details Tile',
        location: ['Left Rail Navigation', 'Build Your Offer'],
        intention: 'View vehicle details',
        tagIndex: 'dc-sci-055'
    },
    [routingConfig.TRADE_IN]: {
        element: 'Trade-In Tile',
        location: ['Left Rail Navigation', 'Build Your Offer'],
        intention: 'View and edit trade-in information',
        tagIndex: 'dc-sci-056'
    },
    [routingConfig.DEAL_SUMMARY]: dealSummaryTileTag,
    [routingConfig.INVENTORY]: {
        element: 'Browse Inventory Link',
        location: ['Left Rail Navigation', 'Quick Links'],
        intention: 'Browse vehicle inventory',
        tagIndex: 'dc-sci-059'
    },
    [routingConfig.MANAGE_DOCUMENTS]: {
        element: 'Manage Documents Link',
        location: ['Left Rail Navigation', 'Quick Links'],
        intention: 'Upload and manage documents',
        tagIndex: 'dc-sci-060'
    }
};

// Generic CDL v3 Event Handler - forwards library-created events
const forwardCDLEvent = (action: any) => {
    /* istanbul ignore if */
    if (!isCdlDataReady) {
        eventsBeforeDataReady.push(action);
        return { event: {} };
    } else {
        // Library already created the complete event data - just forward it
        return buildUserEventV3(action.payload);
    }
};

// Event Map
export const eventMap: EventMapV3 = {
    // Generic CDL v3 Event Forwarder - handles events from any library
    [CDL_V3_EVENT]: forwardCDLEvent,
    [Actions.GET_DETAILS_SUCCESS]: (action: any, currentState: any) => {
        return {
            ...buildCdlV3ObjectFromState(currentState),
            event: {
                action: 'View',
                element: 'Page',
                location: ['Sales Check-In'],
                intention: 'View Sales Check-In Page',
                tagIndex: 'dc-sci-001'
            }
        };
    },
    // Header CTAs
    [Actions.SET_SELECTED_CUSTOMER]: buildEventWrapper({
        action: 'Click',
        element: 'Select Customer Button',
        location: ['Sales Check-In'],
        intention: 'Select an existing customer',
        tagIndex: 'dc-sci-005'
    }),

    // update customer
    [Actions.SET_CUSTOMER_CARD_STATE]: (action: any, currentState: any) => {
        const isEdit = currentState.salesCheckin.common?.customerCardState === CustomerCardState.EDIT;
        if (isEdit) {
            return buildEventWrapper({
                action: 'Click',
                element: 'Edit Customer Button',
                location: ['Sales Check-In'],
                intention: 'Edit existing customer information',
                tagIndex: 'dc-sci-006'
            })(action);
        }
        return { event: {} };
    },
    [Actions.UPDATE_CUSTOMER]: buildEventWrapper({
        action: 'Click',
        element: 'Save Edited Customer Button',
        location: ['Sales Check-In'],
        intention: 'Save changes to customer information',
        tagIndex: 'dc-sci-007'
    }),
    [Actions.CANCEL_UPDATE_CUSTOMER]: buildEventWrapper({
        action: 'Click',
        element: 'Cancel Edit Customer Button',
        location: ['Sales Check-In'],
        intention: 'Cancel editing customer information',
        tagIndex: 'dc-sci-008'
    }),

    // View customer info — isCoBuyer distinguishes dc-sci-063 (co-buyer) from dc-sci-010 (buyer)
    [Actions.VIEW_CUSTOMER_INFO]: (action: AnyFSA) =>
        buildEventWrapper({
            action: 'Click',
            element: action.payload?.isCoBuyer ? 'Open Customer Card for Co-Buyer Link' : 'Open Customer Card for Buyer Link',
            location: action.payload?.isCoBuyer ? ['Customer Bar'] : ['Header', 'Customer Details'],
            intention: action.payload?.isCoBuyer ? 'Edit buyer and co-buyer information' : 'View or edit buyer information',
            tagIndex: action.payload?.isCoBuyer ? 'dc-sci-063' : 'dc-sci-010'
        })(action),

    // Co-Buyer actions
    [Actions.ADD_CO_BUYER]: buildEventWrapper({
        action: 'Click',
        element: 'Add Co-Buyer Link',
        location: ['Sales Check-In'],
        intention: 'Add co-buyer',
        tagIndex: 'dc-sci-011'
    }),
    [Actions.REMOVE_CO_BUYER]: buildEventWrapper({
        action: 'Click',
        element: 'Delete Co-Buyer Icon',
        location: ['Sales Check-In'],
        intention: 'Delete co-buyer',
        tagIndex: 'dc-sci-013'
    }),

    // CVI
    [Actions.BUY_THIS_VEHICLE]: buildEventWrapper({
        action: 'Click',
        element: 'Save Custom Build Vehicle Button',
        location: ['Sales Check-In'],
        intention: 'Save custom build vehicle information',
        tagIndex: 'dc-sci-019'
    }),
    [CHANGE_VEHICLE_TAB_CHANGED]: buildEventWrapper({
        action: 'Click',
        element: `Toggle between Search and Build Tab`,
        location: ['Sales Check-In'],
        intention: `Toggle between Search and Build Tab`,
        tagIndex: ''
    }),

    [NavigationActions.NAVIGATE_TO]: (action: AnyFSA) => {
        const match = navigateToRouteTagMap[action.payload];
        return match ? buildEventWrapper({ action: 'Click', ...match })(action) : { event: {} };
    },

    // Customer Details tile / Insurance quick link both route through SET_FINANCING_INITIAL_TAB,
    // keyed on which sub-tab they open to.
    [FinancingActions.SET_FINANCING_INITIAL_TAB]: (action: AnyFSA) =>
        buildEventWrapper(
            action.payload === 'insurance'
                ? {
                      action: 'Click',
                      element: 'Insurance Link',
                      location: ['Left Rail Navigation', 'Quick Links'],
                      intention: 'Edit insurance information',
                      tagIndex: 'dc-sci-062'
                  }
                : SCI_CDL_TAGS['dc-sci-064']()
        )(action),

    // Keyed on the sub-tab visible at Save, not the entry tab.
    [Actions.UPDATE_PARTICIPANT_DETAILS]: (action: AnyFSA): any => {
        if (action.payload?.subTab === 'insurance') {
            return buildEventWrapper({
                action: 'Click',
                element: 'Save Insurance Button',
                location: ['Insurance'],
                intention: 'Save customer insurance information',
                tagIndex: 'dc-sci-082'
            })(action);
        }
        const role = action.payload?.participant === 'coBuyer' ? 'Co-Buyer' : 'Buyer';
        return buildEventWrapper({
            action: 'Click',
            element: `Save Financing Application for ${role} Button`,
            location: ['Financing', 'Buyer', 'Information'],
            intention: `Edit and save financing information for ${role.toLowerCase()}`,
            tagIndex: 'dc-sci-084'
        })(action);
    },

    // Deal Summary "Send Request" button — REQUEST_PROPOSAL_SEND_REQUEST_CLICKED only fires from this click.
    // Reads requestProposal state directly (not via a selector import) to avoid a circular dependency
    [Actions.REQUEST_PROPOSAL_SEND_REQUEST_CLICKED]: (action: AnyFSA, currentState: any) => ({
        ...buildEventWrapper({
            action: 'Click',
            element: 'Deal Summary Send Request Button',
            location: ['Deal Summary', 'Offer Request'],
            intention: 'Send deal summary to customer',
            tagIndex: 'dc-sci-076'
        })(action),
        deal: {
            preferredPayment: currentState?.requestProposal?.selectedDealTypes?.[0],
            claimedIncentives: currentState?.requestProposal?.selectedIncentives
        }
    }),
    [Actions.UPDATE_ACTIVE_DEAL_VISIBILITY_STATUS]: buildEventWrapper({
        action: 'Click',
        element: 'Submit Change Request Button',
        location: ['Deal Summary', 'Offer Document'],
        intention: 'Submit deal change request',
        tagIndex: 'dc-sci-079'
    }),

    //TRADE IN

    ...tradeInCdlEvents,
    [Actions.SEND_CREDIT_APP_LINK_CDL]: buildEventWrapper({
        action: 'Click',
        element: 'Share Credit App Link',
        location: ['Header', 'Customer Action'],
        intention: 'Share credit app link with customer',
        tagIndex: 'dc-sci-041'
    }),
    [Actions.SEND_DOC_UPLOAD_LINK_CDL]: buildEventWrapper({
        action: 'Click',
        element: 'Send Document Upload Link',
        location: ['Header', 'Customer Action'],
        intention: 'Send document upload link with customer',
        tagIndex: 'dc-sci-042'
    }),

    // Dispatched from useCreditAppMessages on the credit-app iframe's CONFIRMATION_PAGE postMessage.
    [Actions.SUBMIT_CREDIT_APP]: buildEventWrapper({
        action: 'View',
        element: 'Application Complete Confirmation',
        location: ['Financing', 'Application Complete'],
        intention: 'View confirmation of complete credit application',
        tagIndex: 'dc-sci-087'
    }),

    // Fires on every click, unlike FETCH_PDF_SHARE_LINK_REQUEST (skipped when cached/in-flight).
    [Actions.SEND_DEAL_PDF_LINK_CDL]: buildEventWrapper(SCI_CDL_TAGS['dc-sci-065']()),

    [Actions.ACCEPT_DEAL_SUMMARY_REQUEST]: buildEventWrapper(SCI_CDL_TAGS['dc-sci-078']()),

    // Emits only when opened; must stay silent on close (dc-sci-061)
    [Actions.SET_CUSTOMER_VEHICLES_SLIDEOUT_VISIBILITY]: (action: AnyFSA): any => {
        if (action.payload !== true) return undefined;
        return buildEventWrapper(SCI_CDL_TAGS['dc-sci-061']())(action);
    },

    [Actions.REFRESH_OFFER_UPDATES_CDL]: buildEventWrapper(SCI_CDL_TAGS['dc-sci-094']()),

    // Emits only when opened; must stay silent on dismiss (dc-sci-077)
    [Actions.OPEN_REQUEST_CHANGE_MODAL]: (action: AnyFSA): any => {
        if (action.payload !== true) return undefined;
        return buildEventWrapper(SCI_CDL_TAGS['dc-sci-077']())(action);
    }
};

// Middleware
export const cdlV3AnalyticsEventMiddleware = (middlewareArgs: MiddlewareAPI) => {
    const dataReadyEvent = {
        [Actions.CDLV3_DATA_READY]: () => {
            const eventData = {};
            isCdlDataReady = true;
            for (const oldEvent of eventsBeforeDataReady) {
                middlewareArgs.dispatch(oldEvent);
            }
            return buildReadyEventV3(eventData as BaseDataLayerV3);
        }
    };
    const fullEventMap: EventMapV3 = {
        ...dataReadyEvent,
        ...eventMap
    };
    return buildCDLEventMiddlewareV3({
        eventMap: fullEventMap,
        enabled: true,
        middlewareEnabledSelector: () => true,
        alwaysIncludeAppName: true
    })(middlewareArgs);
};
